
import React from "react";
import { Box, Typography, Divider, FormControlLabel, Checkbox, TextField } from "@mui/material";
import { 
  CaseSheetSection, 
  toTitleCase, 
  BORDER, 
  labelSx, 
  rowSx,
  ACCENT,
  LABEL_COLOR,
  SECTION_BAR_BG,
  leftRightCheckboxFields,
  inputSx
} from "./CaseSheetModalComponents";

const DiabeticFootTab = ({ 
  formData, 
  setFormData,
  isFetching, 
  renderTextField, 
  renderSelectField, 
  renderLeftRightCheckbox,
  handleLeftRightText
}) => {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <CaseSheetSection title="FOOT ASSESSMENT" defaultExpanded>
        <Box sx={rowSx}>
          <Typography sx={labelSx}>Status</Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.foot_normal}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setFormData((prev) => {
                    const updates = {
                      ...prev,
                      foot_normal: checked,
                    };
                    if (checked) {
                      leftRightCheckboxFields.forEach((field) => {
                        updates[field] = {
                          left: false,
                          right: false,
                        };
                      });
                      updates.quantitative_avg_vpt = {
                        left: "",
                        right: "",
                      };
                    }
                    return updates;
                  });
                }}
                disabled={isFetching}
                size="small"
                sx={{
                  color: BORDER,
                  "&.Mui-checked": { color: ACCENT },
                  p: 0.5,
                }}
              />
            }
            label={
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 500,
                  color: LABEL_COLOR,
                  userSelect: "none",
                }}
              >
                Normal (No examination required)
              </Typography>
            }
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: { xs: 1.75, sm: 2.25 },
            pointerEvents: formData.foot_normal ? "none" : "auto",
            opacity: formData.foot_normal ? 0.45 : 1,
            transition: "opacity 0.2s ease",
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "minmax(150px, 185px) 1fr",
              },
              gap: { xs: 1, sm: 2 },
              mb: 1,
            }}
          >
            <Box />
            <Box
              sx={{
                display: "flex",
                gap: 3,
                pl: { xs: 0, sm: 0.5 },
              }}
            >
              {["Left", "Right"].map((side) => (
                <Typography
                  key={side}
                  sx={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: SECTION_BAR_BG,
                    letterSpacing: "0.6px",
                    minWidth: 72,
                  }}
                >
                  {side.toUpperCase()}
                </Typography>
              ))}
            </Box>
          </Box>

          {leftRightCheckboxFields.map((fk) =>
            renderLeftRightCheckbox(toTitleCase(fk), fk)
          )}

          <Divider sx={{ borderColor: BORDER, my: 2 }} />

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "minmax(150px, 185px) 1fr 1fr",
              },
              gap: { xs: 1.5, sm: 2 },
              alignItems: "center",
            }}
          >
            <Typography sx={labelSx}>
              Quantitative Avg. VPT
            </Typography>
            <TextField
              size="small"
              value={formData?.quantitative_avg_vpt?.left ?? ""}
              onChange={handleLeftRightText("left")}
              placeholder="Left value"
              disabled={isFetching}
              autoComplete="off"
              inputProps={{ inputMode: "decimal" }}
              sx={inputSx}
              fullWidth
            />
            <TextField
              size="small"
              value={formData?.quantitative_avg_vpt?.right ?? ""}
              onChange={handleLeftRightText("right")}
              placeholder="Right value"
              disabled={isFetching}
              autoComplete="off"
              inputProps={{ inputMode: "decimal" }}
              sx={inputSx}
              fullWidth
            />
          </Box>
        </Box>
      </CaseSheetSection>

      <CaseSheetSection title="FOOT RISK" defaultExpanded>
        {renderSelectField("Left Foot Risk", "left_foot_risk")}
        {renderSelectField("Right Foot Risk", "right_foot_risk")}
      </CaseSheetSection>

      <CaseSheetSection title="INFERENCE" defaultExpanded>
        {renderTextField("Foot Examination", "foot_examination", "Foot examination findings")}
        {renderTextField("Advice", "advice", "Advice")}
      </CaseSheetSection>
    </Box>
  );
};

export default DiabeticFootTab;
